import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Volume2, Loader2, ArrowRight, Check, Mic, Square, Sparkles, Trophy, Clock, BookMarked, Zap, Flame } from "lucide-react";
import client from "@/lib/api";
import { useTts, prefetchTts } from "@/hooks/useTts";
import { useLang } from "@/context/LangContext";
import { translate } from "@/i18n";
import { pickAudioMime, extForMime } from "@/lib/audio";

const L = (k, v) => translate(localStorage.getItem("melletic_lang") || "en", k, v);

export function flatten(steps) {
  const out = [];
  for (const s of steps || []) {
    if (s.type === "mini_test") {
      out.push({ type: "banner", title: L("lesson_mini_test"), subtitle: s.prompt || L("lesson_quick_check") });
      (s.items || []).forEach((it) => out.push(it));
    } else if (s.type === "reading") {
      out.push({ type: "reading_passage", image: s.image, passage_target: s.passage_target });
      (s.questions || []).forEach((q) => out.push({ type: "mcq", ...q }));
    } else if (s.type === "listen_dialogue") {
      out.push({ type: "listen_dialogue_play", audio_text: s.audio_text, transcript: s.transcript, title: s.title });
      (s.questions || []).forEach((q) => out.push({ type: "mcq", ...q }));
    } else out.push(s);
  }
  return out;
}

const norm = (s) => (s || "").toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, "").trim();

export default function Lesson() {
  const { id } = useParams();
  const nav = useNavigate();
  const { play, stop, playing, loading, error: ttsError } = useTts();
  const { t } = useLang();
  const [lesson, setLesson] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro | play | done
  const [screens, setScreens] = useState([]);
  const [idx, setIdx] = useState(0);
  const [mistakes, setMistakes] = useState([]);
  const [result, setResult] = useState(null);
  const [spoke, setSpoke] = useState(false);
  const [answered, setAnswered] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [missedWords, setMissedWords] = useState([]);
  const [missedSteps, setMissedSteps] = useState([]);
  const correctionRef = useRef(false);
  const startRef = useRef(Date.now());
  const [savedState, setSavedState] = useState(null);

  useEffect(() => {
    client.get(`/learn/lesson/${id}`).then(({ data }) => {
      setLesson(data);
      const flat = flatten(data.steps);
      setScreens(flat);
      // Warm the TTS cache for every listening step so playback is instant.
      flat.filter((s) => s && s.audio_text).forEach((s) => prefetchTts(s.audio_text, data.language));
      // Load any saved mid-lesson position so the learner can resume.
      client.get(`/learn/lesson/${id}/state`).then(({ data: st }) => {
        if (st && st.idx > 0 && st.idx < flat.length) setSavedState(st);
      }).catch(() => {});
    }).catch(() => { toast.error(L("lesson_not_available")); nav("/learn"); });
  }, [id, nav]);

  const step = screens[idx];
  const progress = screens.length ? Math.round((idx / screens.length) * 100) : 0;

  // STOP any static audio when the exercise/step changes (no bleed-over, no overlap).
  useEffect(() => { stop(); }, [idx, stop]);

  // Autosave mid-lesson position (main pass only) so a long lesson can be resumed.
  useEffect(() => {
    if (phase === "play" && !correctionRef.current && idx > 0 && screens.length && !result) {
      client.post(`/learn/lesson/${id}/state`, { idx, correct, answered }).catch(() => {});
    }
  }, [idx, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mastery grading: track first-pass accuracy + the words the learner missed (for recycling).
  const grade = (gstep, ok) => {
    const w = gstep?.word;
    if (!correctionRef.current) {
      setAnswered((a) => a + 1);
      if (ok) setCorrect((c) => c + 1);
      else {
        setMissedSteps((p) => (p.includes(gstep) ? p : [...p, gstep]));
        setMistakes((p) => [...p, { prompt: gstep.prompt, correct: gstep.explain || "" }]);
        if (w) setMissedWords((p) => (p.includes(w) ? p : [...p, w]));
      }
    } else if (ok && w) {
      setMissedWords((p) => p.filter((x) => x !== w)); // fixed in the correction round
    }
  };

  const next = () => {
    if (idx + 1 < screens.length) { setIdx((i) => i + 1); return; }
    // End of the main pass: if the learner missed things, run ONE intelligent
    // correction round that re-tests just those items (recycle, don't repeat the whole lesson).
    if (!correctionRef.current && missedSteps.length > 0) {
      correctionRef.current = true;
      setScreens([{ type: "banner", title: L("lesson_fix_title"), subtitle: L("lesson_fix_sub") }, ...missedSteps]);
      setIdx(0);
      return;
    }
    finish();
  };

  const finish = async () => {
    const minutes = Math.max(1, Math.round((Date.now() - startRef.current) / 60000));
    const accuracy = answered ? Math.round((correct / answered) * 100) : 100;
    // Mastery requires genuine EVIDENCE: high first-try accuracy AND a sufficient
    // number of graded interactions. A tiny, easy quiz can be "completed" but not "mastered".
    const MIN_GRADED = 12;
    const mastered = accuracy >= 75 && answered >= MIN_GRADED;
    try {
      const { data } = await client.post(`/learn/lesson/${id}/complete`, {
        minutes, speaking_seconds: spoke ? 20 : 0, mistakes,
        accuracy, mastered, missed_words: missedWords,
      });
      setResult({ ...data, accuracy, mastered });
      setPhase("done");
      window.dispatchEvent(new Event("mel:progress-refresh"));
    } catch (e) {
      toast.error(L("lesson_save_failed"));
    }
  };

  if (!lesson) return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  // ---------- INTRO ----------
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <div className="relative h-64">
          <img src={lesson.cover} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#F8FAFC] via-transparent to-[#0F172A]/30" />
          <button onClick={() => nav("/learn")} data-testid="lesson-close" className="absolute top-5 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow"><X size={20} /></button>
        </div>
        <div className="flex-1 px-6 -mt-10 relative">
          <div className="bg-white rounded-3xl p-6 shadow-[0_18px_44px_-24px_rgba(15,23,42,0.4)] border border-slate-100">
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#2563EB]">{t("lesson_label", { n: lesson.n })}</p>
            <h1 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-1">{lesson.title}</h1>
            <p className="text-slate-500 mt-1">{lesson.objective}</p>
            <div className="mt-4 flex gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1"><Clock size={15} /> {lesson.minutes} {t("common_min")}</span>
              <span className="flex items-center gap-1"><Sparkles size={15} /> +{lesson.xp} XP</span>
              <span className="flex items-center gap-1"><BookMarked size={15} /> {t("lesson_words_n", { n: lesson.vocab?.length || 0 })}</span>
            </div>
          </div>
          {savedState && savedState.idx > 0 && savedState.idx < screens.length ? (
            <>
              <button onClick={() => { startRef.current = Date.now(); setCorrect(savedState.correct || 0); setAnswered(savedState.answered || 0); setIdx(savedState.idx); setPhase("play"); }} data-testid="continue-lesson-btn"
                className="w-full mt-6 bg-[#2563EB] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_14px_34px_-14px_rgba(30,86,160,0.9)]">
                {t("lesson_continue")} <ArrowRight size={18} />
              </button>
              <button onClick={() => { setSavedState(null); setIdx(0); setCorrect(0); setAnswered(0); startRef.current = Date.now(); setPhase("play"); }} data-testid="restart-lesson-btn"
                className="w-full mt-3 bg-white border border-slate-200 text-slate-600 font-semibold py-3 rounded-2xl">
                {t("lesson_restart")}
              </button>
            </>
          ) : (
            <button onClick={() => { startRef.current = Date.now(); setPhase("play"); }} data-testid="start-lesson-btn"
              className="w-full mt-6 bg-[#2563EB] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_14px_34px_-14px_rgba(30,86,160,0.9)]">
              {t("lesson_start")} <ArrowRight size={18} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---------- DONE ----------
  if (phase === "done") {
    const r = result || {};
    const metrics = [
      { label: L("m_minutes_learning"), val: `${r.minutes || 0}m`, icon: Clock, tint: "#2563EB" },
      { label: L("m_new_words"), val: `+${r.new_words || 0}`, icon: BookMarked, tint: "#3B82F6" },
      { label: L("m_words_practiced"), val: r.practiced_words || 0, icon: Sparkles, tint: "#8B5CF6" },
      { label: L("m_exercises"), val: r.exercises || 0, icon: Check, tint: "#2563EB" },
      { label: L("m_speaking"), val: r.speaking_activities || 0, icon: Mic, tint: "#3B82F6" },
      { label: L("m_to_review"), val: r.mistakes || 0, icon: Zap, tint: r.mistakes ? "#F59E0B" : "#8B5CF6" },
    ];
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center px-6 pt-16 pb-10 text-center overflow-y-auto">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2563EB] to-[#3B82F6] flex items-center justify-center shadow-[0_18px_44px_-16px_rgba(37,99,235,0.85)] animate-pop" data-testid="lesson-complete">
          <Trophy className="text-white" size={38} />
        </div>
        <h1 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-5">{t("lesson_complete_title")}</h1>
        <p className="text-[#475569] mt-1">{lesson.title}</p>

        <div className={`mt-5 w-full max-w-[360px] rounded-2xl px-4 py-3 flex items-center justify-between border ${r.mastered ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="lesson-mastery">
          <div className="flex items-center gap-2">
            <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${r.mastered ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {r.mastered ? <Check size={18} /> : <Zap size={18} />}
            </span>
            <div className="text-left">
              <p className={`font-bold text-sm ${r.mastered ? "text-emerald-800" : "text-amber-800"}`}>{r.mastered ? L("mastery_mastered") : L("mastery_keep_practicing")}</p>
              <p className="text-[11px] text-[#475569]">{L("mastery_accuracy", { p: r.accuracy ?? 100 })}</p>
            </div>
          </div>
          {!r.mastered && (
            <button onClick={() => nav("/review")} data-testid="mastery-review-btn" className="text-xs font-bold text-amber-800 bg-white border border-amber-300 rounded-xl px-3 py-2">{L("mastery_review_now")}</button>
          )}
        </div>

        <div className="mt-6 w-full max-w-[360px] grid grid-cols-3 gap-3">
          {metrics.map((m) => (
            <div key={m.label} className="rounded-2xl bg-white border border-slate-200/70 p-3">
              <span className="w-8 h-8 rounded-xl flex items-center justify-center mx-auto" style={{ background: `${m.tint}18`, color: m.tint }}><m.icon size={16} /></span>
              <p className="font-heading font-extrabold text-[#0F172A] mt-1.5">{m.val}</p>
              <p className="text-[10px] text-[#475569] leading-tight mt-0.5">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 w-full max-w-[360px] rounded-2xl bg-white border border-slate-200/70 p-4 text-left">
          <div className="flex items-center justify-between text-xs font-bold text-[#475569]">
            <span>{t("done_level_progress", { lv: r.level || "A1" })}</span>
            <span className="text-[#2563EB]">{r.level_pct_before ?? 0}% → {r.level_pct_after ?? 0}%</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-[#EDF2F7] overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#3B82F6] transition-all duration-700" style={{ width: `${r.level_pct_after || 0}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-[#475569]">{t("home_module")}</span>
            <b className="text-[#0F172A]">{r.module_done || 0} / {r.module_total || 0}</b>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-sm">
            <span className="text-[#475569] flex items-center gap-1.5"><Flame size={15} className="text-[#2563EB]" /> {t("done_streak")}</span>
            <b className="text-[#0F172A]">{t(r.streak === 1 ? "done_day" : "done_days", { n: r.streak || 0 })}</b>
          </div>
        </div>

        {result?.achievements?.length > 0 && (
          <div className="mt-4 w-full max-w-[360px] space-y-2">
            {result.achievements.map((a) => (
              <div key={a.code} className="flex items-center gap-3 bg-[#E0F2FE] border border-[#2563EB]/20 rounded-2xl px-4 py-3" data-testid={`achievement-${a.code}`}>
                <Trophy size={18} className="text-[#2563EB]" />
                <div className="text-left"><p className="font-bold text-[#0F172A] text-sm">{a.title}</p><p className="text-xs text-[#475569]">{a.description}</p></div>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => nav("/home")} data-testid="lesson-continue-home"
          className="w-full max-w-[360px] mt-7 bg-[#2563EB] text-white font-bold py-4 rounded-2xl shadow-[0_16px_30px_-14px_rgba(37,99,235,0.85)]">{t("common_continue")}</button>
        <button onClick={() => nav("/challenge")} data-testid="lesson-challenge-btn"
          className="w-full max-w-[360px] mt-3 border border-[#2563EB]/30 bg-[#2563EB]/5 text-[#2563EB] font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2"><Zap size={17} /> {t("challenge_title")}</button>
      </div>
    );
  }

  // ---------- PLAY ----------
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <div className="flex items-center gap-3 px-4 pt-6 pb-3">
        <button onClick={() => nav("/learn")} data-testid="lesson-quit"><X size={22} className="text-slate-400" /></button>
        <div className="flex-1 h-2.5 rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full bg-[#2563EB] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
        <span className="text-xs font-bold text-slate-400">{idx + 1}/{screens.length}</span>
      </div>
      <div className="flex-1 px-5 pb-6 overflow-y-auto">
        <StepView key={idx} step={step} onNext={next} play={play} playing={playing} ttsLoading={loading} ttsError={ttsError}
          lang={lesson.language} grade={grade} onSpoke={() => setSpoke(true)} />
      </div>
    </div>
  );
}

// ================= Step renderer =================
export function StepView({ step, onNext, play, playing, ttsLoading, ttsError, lang, grade, onSpoke }) {
  if (!step) return null;
  const t = step.type;
  const audio = { play, playing, ttsLoading, ttsError, lang };
  if (t === "scene") return <SceneStep step={step} onNext={onNext} {...audio} />;
  if (t === "banner") return <BannerStep step={step} onNext={onNext} />;
  if (t === "vocab") return <VocabStep step={step} onNext={onNext} {...audio} />;
  if (t === "reading_passage") return <ReadingPassage step={step} onNext={onNext} {...audio} />;
  if (t === "listen_dialogue_play") return <ListenDialoguePlay step={step} onNext={onNext} {...audio} />;
  if (t === "mcq" || t === "listen_choose" || t === "truefalse") return <ChoiceStep step={step} onNext={onNext} {...audio} grade={grade} />;
  if (t === "image_choice") return <ImageChoiceStep step={step} onNext={onNext} grade={grade} />;
  if (t === "match") return <MatchStep step={step} onNext={onNext} />;
  if (t === "order") return <OrderStep step={step} onNext={onNext} grade={grade} />;
  if (t === "fill") return <FillStep step={step} onNext={onNext} grade={grade} />;
  if (t === "listen_type") return <ListenTypeStep step={step} onNext={onNext} {...audio} grade={grade} />;
  if (t === "writing") return <WritingStep step={step} onNext={onNext} />;
  if (t === "speak") return <SpeakStep step={step} onNext={onNext} {...audio} onSpoke={onSpoke} />;
  return <BannerStep step={{ title: "Step", subtitle: "" }} onNext={onNext} />;
}

const PrimaryBtn = ({ children, ...p }) => (
  <button {...p} className="w-full mt-6 bg-[#2563EB] text-white font-bold py-4 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2">{children}</button>
);
// Small speaker button. READY -> tap -> LOADING (spinner) -> PLAYING (pulse) -> READY. Retry on error.
const SpeakerBtn = ({ onClick, playing, loading, error }) => {
  const state = loading ? "loading" : playing ? "playing" : error ? "error" : "idle";
  return (
    <button onClick={onClick} data-testid="audio-btn" aria-label="Play audio"
      className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-colors ${
        state === "playing" ? "bg-[#2563EB] text-white animate-pulse"
        : state === "error" ? "bg-rose-100 text-rose-600"
        : "bg-[#2563EB]/10 text-[#2563EB]"}`}>
      {state === "loading" ? <Loader2 size={18} className="animate-spin" /> : <Volume2 size={20} />}
    </button>
  );
};
// Large round listen button used by listen_choose / listen_type. Same READY->LOADING->PLAYING states.
const BigListenBtn = ({ onClick, playing, loading, error, ...p }) => (
  <button onClick={onClick} data-testid="listen-play" aria-label="Play audio"
    className={`w-20 h-20 rounded-full flex items-center justify-center text-white shadow-[0_14px_34px_-14px_rgba(30,86,160,0.9)] transition-colors ${
      error ? "bg-rose-500" : "bg-[#2563EB]"} ${playing ? "animate-pulse" : ""}`} {...p}>
    {loading ? <Loader2 size={34} className="animate-spin" /> : <Volume2 size={34} />}
  </button>
);
const Overline = ({ children }) => <p className="text-[11px] font-bold uppercase tracking-widest text-[#2563EB]">{children}</p>;

function SceneStep({ step, onNext, play, playing, ttsLoading, ttsError, lang }) {
  const key = step.audio_text || step.text_target;
  return (
    <div className="pt-2" data-testid="step-scene">
      {step.image && <img src={step.image} alt="" className="w-full h-52 object-cover rounded-3xl" />}
      <div className="mt-5 bg-white rounded-3xl p-5 border border-slate-100 shadow-sm">
        {step.character && <Overline>{step.character}</Overline>}
        <h2 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-1">{step.title}</h2>
        {step.text_target && (
          <div className="mt-3 flex items-center gap-3">
            <SpeakerBtn onClick={() => play(key, lang)} playing={playing === key} loading={ttsLoading === key} error={ttsError === key} />
            <div><p className="font-bold text-[#0F172A]">{step.text_target}</p><p className="text-sm text-slate-500">{step.text_native}</p></div>
          </div>
        )}
        {step.body && <p className="text-slate-500 text-sm mt-4">{step.body}</p>}
      </div>
      <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>
    </div>
  );
}

function BannerStep({ step, onNext }) {
  return (
    <div className="pt-16 text-center" data-testid="step-banner">
      <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center mx-auto"><Check size={30} /></div>
      <h2 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-4">{step.title}</h2>
      <p className="text-slate-500 mt-1">{step.subtitle}</p>
      <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("lesson_banner_start")} <ArrowRight size={18} /></PrimaryBtn>
    </div>
  );
}

function VocabStep({ step, onNext, play, playing, ttsLoading, ttsError, lang }) {
  const key = step.audio_text || step.word;
  return (
    <div className="pt-2" data-testid="step-vocab">
      <Overline>{L("ov_new_word")}</Overline>
      {step.image && <img src={step.image} alt="" className="w-full h-44 object-cover rounded-3xl mt-2" />}
      <div className="mt-4 bg-white rounded-3xl p-6 border border-slate-100 shadow-sm text-center">
        <div className="flex items-center justify-center gap-3">
          <h2 className="font-heading text-3xl font-extrabold text-[#0F172A]">{step.word}</h2>
          <SpeakerBtn onClick={() => play(key, lang)} playing={playing === key} loading={ttsLoading === key} error={ttsError === key} />
        </div>
        <p className="text-slate-500 mt-1">{step.translation}</p>
        {step.example && <div className="mt-4 bg-[#EBF3FA] rounded-2xl p-3"><p className="text-[#0F172A] font-medium text-sm">{step.example}</p><p className="text-slate-500 text-xs mt-0.5">{step.example_translation}</p></div>}
      </div>
      <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>
    </div>
  );
}

function ReadingPassage({ step, onNext, play, playing, ttsLoading, ttsError, lang }) {
  const key = step.passage_target;
  return (
    <div className="pt-2" data-testid="step-reading">
      <Overline>{L("ov_read")}</Overline>
      {step.image && <img src={step.image} alt="" className="w-full h-40 object-cover rounded-3xl mt-2" />}
      <div className="mt-4 bg-white rounded-3xl p-5 border border-slate-100 shadow-sm flex items-start gap-3">
        <SpeakerBtn onClick={() => play(key, lang)} playing={playing === key} loading={ttsLoading === key} error={ttsError === key} />
        <p className="text-[#0F172A] leading-relaxed">{step.passage_target}</p>
      </div>
      <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>
    </div>
  );
}

function Feedback({ ok, explain }) {
  return (
    <div className={`mt-3 flex items-start gap-2 text-sm ${ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="feedback">
      <span className={`mt-px w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${ok ? "bg-emerald-100" : "bg-rose-100"}`}>
        {ok ? <Check size={13} /> : <X size={13} />}
      </span>
      <p className="leading-snug"><b>{ok ? L("lesson_correct") : L("lesson_wrong")}</b>{explain ? <span className="text-[#475569] font-normal"> — {explain}</span> : ""}</p>
    </div>
  );
}

function ListenDialoguePlay({ step, onNext, play, playing, ttsLoading, lang }) {
  const [showT, setShowT] = useState(false);
  const [listened, setListened] = useState(false);
  const speak = () => { setListened(true); play(step.audio_text, lang); };
  return (
    <div className="pt-4" data-testid="step-listen-dialogue">
      <div className="rounded-3xl bg-[#0F172A] text-white p-6">
        <p className="text-xs font-bold uppercase tracking-wide text-white/50">{L("ld_kicker")}</p>
        <h2 className="font-heading text-xl font-extrabold mt-1">{step.title || L("ld_title")}</h2>
        <p className="text-white/70 text-sm mt-2">{L("ld_hint")}</p>
        <button onClick={speak} disabled={ttsLoading} data-testid="ld-play"
          className="mt-5 w-full bg-white text-[#0F172A] font-bold py-4 rounded-2xl flex items-center justify-center gap-2">
          {ttsLoading ? <Loader2 size={18} className="animate-spin" /> : <Volume2 size={18} />}
          {listened ? L("ld_replay") : L("ld_play")}
        </button>
      </div>
      <button onClick={() => setShowT((v) => !v)} data-testid="ld-transcript-toggle"
        className="mt-3 text-sm font-semibold text-[#2563EB]">
        {showT ? L("ld_hide_transcript") : L("ld_show_transcript")}
      </button>
      {showT && (
        <div className="mt-2 space-y-2 rounded-2xl bg-slate-50 border border-slate-200 p-4" data-testid="ld-transcript">
          {(step.transcript || []).map((line, i) => (
            <p key={i} className="text-sm text-[#0F172A]"><span className="font-bold">{line.speaker}: </span>{line.text}</p>
          ))}
        </div>
      )}
      <PrimaryBtn onClick={onNext} disabled={!listened} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>
    </div>
  );
}

function ChoiceStep({ step, onNext, play, playing, ttsLoading, ttsError, lang, grade }) {
  const [sel, setSel] = useState(null);
  const [checked, setChecked] = useState(false);
  const isTF = step.type === "truefalse";
  const options = isTF ? ["True", "False"] : step.options;
  const answer = isTF ? (step.is_true ? 0 : 1) : step.answer;
  const isListen = step.type === "listen_choose";
  const ok = sel === answer;
  const check = () => {
    setChecked(true);
    grade(step, ok);
  };
  return (
    <div className="pt-4" data-testid="step-choice">
      {isListen ? (
        <div className="flex flex-col items-center py-4">
          <BigListenBtn onClick={() => play(step.audio_text, lang)} playing={playing === step.audio_text} loading={ttsLoading === step.audio_text} error={ttsError === step.audio_text} />
          <p className="mt-3 font-semibold text-[#0F172A]">{step.prompt || L("lt_tap_listen")}</p>
        </div>
      ) : (
        <>
          {step.image && <img src={step.image} alt="" data-testid="choice-image" className="w-full h-44 object-cover rounded-3xl mb-4" />}
          <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{step.prompt}</h2>
        </>
      )}
      <div className="mt-5 space-y-3">
        {options.map((o, i) => {
          let cls = "border-slate-200 bg-white text-[#0F172A]";
          if (checked && i === answer) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
          else if (checked && i === sel) cls = "border-rose-400 bg-rose-50 text-rose-700";
          else if (sel === i) cls = "border-[#2563EB] bg-[#2563EB]/5 text-[#0F172A]";
          return (
            <button key={i} disabled={checked} onClick={() => setSel(i)} data-testid={`option-${i}`}
              className={`w-full text-left rounded-2xl px-4 py-3.5 border font-medium ${cls}`}>{o}</button>
          );
        })}
      </div>
      {checked && <Feedback ok={ok} explain={step.explain} />}
      {!checked ? <PrimaryBtn onClick={check} disabled={sel === null} data-testid="check-btn">{L("common_check")}</PrimaryBtn>
        : <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>}
    </div>
  );
}

function ImageChoiceStep({ step, onNext, grade }) {
  const [sel, setSel] = useState(null); const [checked, setChecked] = useState(false);
  const ok = sel === step.answer;
  return (
    <div className="pt-4" data-testid="step-image-choice">
      <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{step.prompt}</h2>
      <div className="mt-5 grid grid-cols-2 gap-3">
        {step.options.map((o, i) => {
          let ring = "border-slate-200";
          if (checked && i === step.answer) ring = "border-emerald-400";
          else if (checked && i === sel) ring = "border-rose-400";
          else if (sel === i) ring = "border-[#2563EB]";
          return (
            <button key={i} disabled={checked} onClick={() => setSel(i)} data-testid={`img-option-${i}`}
              className={`rounded-2xl overflow-hidden border-2 bg-white ${ring}`}>
              <img src={o.image} alt="" className="w-full h-28 object-cover" />
              {checked && o.label ? <p className="py-2 text-sm font-semibold text-[#0F172A]">{o.label}</p> : <div className="py-1.5" />}
            </button>
          );
        })}
      </div>
      {checked && <Feedback ok={ok} />}
      {!checked ? <PrimaryBtn onClick={() => { setChecked(true); grade(step, ok); }} disabled={sel === null} data-testid="check-btn">{L("common_check")}</PrimaryBtn>
        : <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>}
    </div>
  );
}

function MatchStep({ step, onNext }) {
  const lefts = step.pairs.map((p) => p.a);
  const rights = useMemo(() => [...step.pairs.map((p) => p.b)].sort(() => Math.random() - 0.5), []); // eslint-disable-line
  const [selL, setSelL] = useState(null);
  const [matched, setMatched] = useState({});
  const [wrong, setWrong] = useState(null);
  const done = Object.keys(matched).length === lefts.length;
  const pick = (side, val) => {
    if (side === "L") setSelL(val);
    else if (selL != null) {
      const correct = step.pairs.find((p) => p.a === selL)?.b === val;
      if (correct) { setMatched((m) => ({ ...m, [selL]: val })); setSelL(null); }
      else { setWrong(val); setTimeout(() => setWrong(null), 500); }
    }
  };
  return (
    <div className="pt-4" data-testid="step-match">
      <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{step.prompt}</h2>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="space-y-3">
          {lefts.map((l) => (
            <button key={l} disabled={matched[l]} onClick={() => pick("L", l)} data-testid={`match-left-${l}`}
              className={`w-full rounded-2xl px-3 py-3 border font-semibold text-sm ${matched[l] ? "border-emerald-300 bg-emerald-50 text-emerald-700" : selL === l ? "border-[#2563EB] bg-[#2563EB]/5" : "border-slate-200 bg-white text-[#0F172A]"}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-3">
          {rights.map((r) => {
            const used = Object.values(matched).includes(r);
            return (
              <button key={r} disabled={used} onClick={() => pick("R", r)} data-testid={`match-right-${r}`}
                className={`w-full rounded-2xl px-3 py-3 border font-semibold text-sm ${used ? "border-emerald-300 bg-emerald-50 text-emerald-700" : wrong === r ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white text-[#0F172A]"}`}>{r}</button>
            );
          })}
        </div>
      </div>
      <PrimaryBtn onClick={onNext} disabled={!done} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>
    </div>
  );
}

function OrderStep({ step, onNext, grade }) {
  const bank = useMemo(() => [...step.words].sort(() => Math.random() - 0.5), []); // eslint-disable-line
  const [built, setBuilt] = useState([]);
  const [checked, setChecked] = useState(false);
  const used = built.map((b) => b.k);
  const ok = JSON.stringify(built.map((b) => b.w)) === JSON.stringify(step.answer);
  return (
    <div className="pt-4" data-testid="step-order">
      <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{step.prompt}</h2>
      <div className="mt-5 min-h-[60px] rounded-2xl border-2 border-dashed border-slate-200 bg-white p-3 flex flex-wrap gap-2" data-testid="order-answer">
        {built.map((b) => (
          <button key={b.k} disabled={checked} onClick={() => setBuilt((p) => p.filter((x) => x.k !== b.k))}
            className="px-3 py-2 rounded-xl bg-[#2563EB]/10 text-[#2563EB] font-semibold text-sm">{b.w}</button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {bank.map((w, i) => (
          <button key={i} disabled={used.includes(i) || checked} onClick={() => setBuilt((p) => [...p, { k: i, w }])}
            data-testid={`chip-${i}`}
            className={`px-3 py-2 rounded-xl border font-semibold text-sm ${used.includes(i) ? "opacity-30 border-slate-200 bg-slate-50" : "border-slate-200 bg-white text-[#0F172A]"}`}>{w}</button>
        ))}
      </div>
      {checked && <Feedback ok={ok} explain={ok ? "" : L("lesson_correct_answer", { a: step.answer.join(" ") })} />}
      {!checked ? <PrimaryBtn onClick={() => { setChecked(true); grade(step, ok); }} disabled={built.length !== step.words.length} data-testid="check-btn">{L("common_check")}</PrimaryBtn>
        : <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>}
    </div>
  );
}

function FillStep({ step, onNext, grade }) {
  const [sel, setSel] = useState(null); const [checked, setChecked] = useState(false);
  const ok = sel === step.answer;
  return (
    <div className="pt-4" data-testid="step-fill">
      <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{step.prompt}</h2>
      <div className="mt-5 flex flex-wrap gap-3">
        {step.options.map((o, i) => {
          let cls = "border-slate-200 bg-white text-[#0F172A]";
          if (checked && i === step.answer) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
          else if (checked && i === sel) cls = "border-rose-400 bg-rose-50 text-rose-700";
          else if (sel === i) cls = "border-[#2563EB] bg-[#2563EB]/5";
          return <button key={i} disabled={checked} onClick={() => setSel(i)} data-testid={`fill-${i}`} className={`px-4 py-3 rounded-2xl border font-semibold ${cls}`}>{o}</button>;
        })}
      </div>
      {checked && <Feedback ok={ok} />}
      {!checked ? <PrimaryBtn onClick={() => { setChecked(true); grade(step, ok); }} disabled={sel === null} data-testid="check-btn">{L("common_check")}</PrimaryBtn>
        : <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>}
    </div>
  );
}

function ListenTypeStep({ step, onNext, play, playing, ttsLoading, ttsError, lang, grade }) {
  const [val, setVal] = useState(""); const [checked, setChecked] = useState(false);
  const ok = norm(val) === norm(step.answer);
  return (
    <div className="pt-4" data-testid="step-listen-type">
      <div className="flex flex-col items-center py-3">
        <BigListenBtn onClick={() => play(step.audio_text, lang)} playing={playing === step.audio_text} loading={ttsLoading === step.audio_text} error={ttsError === step.audio_text} />
        <p className="mt-3 font-semibold text-[#0F172A]">{L("lt_type_hear")}</p>
      </div>
      <input value={val} onChange={(e) => setVal(e.target.value)} disabled={checked} data-testid="listen-input"
        className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 outline-none focus:border-[#2563EB]" placeholder={L("lt_type_here")} />
      {checked && <Feedback ok={ok} explain={ok ? "" : L("lt_answer", { a: step.answer })} />}
      {!checked ? <PrimaryBtn onClick={() => { setChecked(true); grade(step, ok); }} disabled={!val.trim()} data-testid="check-btn">{L("common_check")}</PrimaryBtn>
        : <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>}
    </div>
  );
}

function WritingStep({ step, onNext }) {
  const [val, setVal] = useState(""); const [submitted, setSubmitted] = useState(false);
  return (
    <div className="pt-4" data-testid="step-writing">
      <Overline>{L("ov_write")}</Overline>
      <h2 className="font-heading text-xl font-extrabold text-[#0F172A] mt-1">{step.prompt}</h2>
      <textarea value={val} onChange={(e) => setVal(e.target.value)} disabled={submitted} rows={3} data-testid="writing-input"
        className="w-full mt-4 rounded-2xl border border-slate-200 px-4 py-3.5 outline-none focus:border-[#2563EB]" placeholder={L("w_placeholder")} />
      {submitted && step.sample_answer && <div className="mt-4 bg-[#EBF3FA] rounded-2xl p-3"><p className="text-xs text-slate-500">{L("w_sample")}</p><p className="font-semibold text-[#0F172A]">{step.sample_answer}</p></div>}
      {!submitted ? <PrimaryBtn onClick={() => setSubmitted(true)} disabled={!val.trim()} data-testid="check-btn">{L("common_submit")}</PrimaryBtn>
        : <PrimaryBtn onClick={onNext} data-testid="step-continue">{L("common_continue")} <ArrowRight size={18} /></PrimaryBtn>}
    </div>
  );
}

function SpeakStep({ step, onNext, play, playing, ttsLoading, ttsError, lang, onSpoke }) {
  const [rec, setRec] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const mediaRef = useRef(null); const chunks = useRef([]);
  const key = step.audio_text || step.target_text;

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickAudioMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined); mediaRef.current = mr; chunks.current = [];
      mr.ondataavailable = (e) => chunks.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || mime || "audio/webm";
        const blob = new Blob(chunks.current, { type });
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, `speech.${extForMime(type)}`);
          fd.append("target", step.target_text);
          fd.append("lang", lang);
          const { data } = await client.post("/learn/speech-check", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setRes(data); onSpoke();
        } catch { setRes({ score: 0, said: "", pass: false, error: true }); }
        finally { setBusy(false); }
      };
      mr.start(); setRec(true);
    } catch { toast.error(L("common_mic_unavailable")); }
  };
  const stop = () => { mediaRef.current?.stop(); setRec(false); };

  return (
    <div className="pt-4 text-center" data-testid="step-speak">
      <Overline>{L("ov_speak")}</Overline>
      <div className="mt-4 bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
        <p className="font-heading text-2xl font-extrabold text-[#0F172A]">{step.target_text}</p>
        <p className="text-slate-500 mt-1">{step.translation}</p>
        <button onClick={() => play(key, lang)} data-testid="speak-listen" className={`mt-3 inline-flex items-center gap-2 font-semibold text-sm ${ttsError === key ? "text-rose-600" : "text-[#2563EB]"}`}>
          {ttsLoading === key ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={17} />} {ttsError === key ? L("common_retry") : L("common_listen")}
        </button>
      </div>
      <button onClick={rec ? stop : start} disabled={busy} data-testid="mic-orb-button"
        className={`mt-6 w-24 h-24 rounded-full flex items-center justify-center mx-auto shadow-[0_18px_44px_-16px_rgba(30,86,160,0.9)] ${rec ? "bg-rose-500 animate-pulse" : "bg-[#2563EB]"} text-white`}>
        {busy ? <Loader2 className="animate-spin" size={34} /> : rec ? <Square size={30} /> : <Mic size={34} />}
      </button>
      <p className="text-slate-400 text-sm mt-3">{busy ? L("sp_checking") : rec ? L("sp_listening") : L("sp_tap")}</p>
      {res && !res.error && (
        <div className={`mt-4 rounded-2xl p-4 ${res.pass ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`} data-testid="speak-result">
          <p className={`font-bold ${res.pass ? "text-emerald-700" : "text-amber-700"}`}>{L("sp_score", { s: res.score })}</p>
          {res.said && <p className="text-sm text-slate-500 mt-1">{L("sp_heard", { s: res.said })}</p>}
        </div>
      )}
      <div className="flex gap-3 mt-6">
        <button onClick={onNext} data-testid="speak-skip" className="flex-1 border border-slate-200 bg-white text-slate-500 font-semibold py-3.5 rounded-2xl">{L("common_skip")}</button>
        {res && <button onClick={onNext} data-testid="step-continue" className="flex-1 bg-[#2563EB] text-white font-bold py-3.5 rounded-2xl">{L("common_continue")}</button>}
      </div>
    </div>
  );
}
